import { Loader2 } from 'lucide-react';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { useDocument } from '@/hooks/use-documents';
import { PdfViewer } from './PdfViewer';
import { DocumentDataPanel } from './DocumentDataPanel';
import { DocumentPrintHost } from './DocumentPrintView';

interface DocumentWorkspaceProps {
  documentId: string;
  /**
   * Optional content rendered inside the right pane, above the data
   * panel. Task surface uses this for the AttachmentLabelsBar; the
   * standalone detail page leaves it undefined.
   */
  rightPaneAccessory?: React.ReactNode;
  /**
   * Forwarded to the data panel's `onDirtyChange` so callers (the
   * standalone detail page) can hook unsaved-changes navigation guards.
   * The task surface doesn't track dirty state — closing the tab
   * autosaves are handled elsewhere.
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Forwarded to the print view so the printed document includes the
   * attachment's classification labels in its header.
   */
  attachmentLabels?: string[];
}

/**
 * Two-pane PDF + extracted-data workspace, plus the print portal.
 * Rendered by both the standalone `/documents/:id` route
 * and each extraction tab inside the task work panel — extracting it
 * here keeps the panel sizing, the loading state, the resize handle
 * placement, and the print plumbing in one place. A drift in any of
 * those previously needed two edits.
 *
 * Layout:
 *
 *   ┌──────────────────────┬─┬───────────────────────┐
 *   │                      │ │  rightPaneAccessory   │
 *   │      PdfViewer       │ ├───────────────────────┤
 *   │                      │ │  DataPanel (form,     │
 *   │                      │ │  internally scrolls)  │
 *   └──────────────────────┴─┴───────────────────────┘
 *                            ▲
 *                       ResizableHandle
 *
 *   + portal: <DocumentPrintHost> attached to <body>
 */
export function DocumentWorkspace({
  documentId,
  rightPaneAccessory,
  onDirtyChange,
  attachmentLabels,
}: DocumentWorkspaceProps): React.ReactElement {
  const { data: document, isLoading, error } = useDocument(documentId);

  // Loading / error / not-found all wrap the right-pane accessory so
  // the AttachmentLabelsBar (or whatever the caller passed) doesn't pop
  // in only after the data lands. Layout shift would otherwise jolt the
  // form area when switching between extraction tabs.
  if (isLoading) {
    return (
      <PrePaneState rightPaneAccessory={rightPaneAccessory}>
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </PrePaneState>
    );
  }

  if (error) {
    // Transient API failure — distinct from a real 404 below. Tell the
    // user there was a problem rather than the misleading "not found".
    return (
      <PrePaneState rightPaneAccessory={rightPaneAccessory}>
        <div className="text-center text-sm text-destructive">
          Failed to load document extraction. Try refreshing the page.
        </div>
      </PrePaneState>
    );
  }

  if (!document) {
    return (
      <PrePaneState rightPaneAccessory={rightPaneAccessory}>
        <div className="text-sm text-muted-foreground">Document extraction not found</div>
      </PrePaneState>
    );
  }

  return (
    <ResizablePanelGroup orientation="horizontal" className="h-full">
      <ResizablePanel defaultSize={50} minSize="30">
        <PdfViewer documentId={documentId} />
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel defaultSize={50} minSize="30">
        <div className="flex h-full flex-col">
          {rightPaneAccessory}
          <div className="min-h-0 flex-1 overflow-auto">
            <DocumentDataPanel document={document} onDirtyChange={onDirtyChange} />
          </div>
        </div>
      </ResizablePanel>
      <DocumentPrintHost document={document} attachmentLabels={attachmentLabels} />
    </ResizablePanelGroup>
  );
}

/**
 * Renders the accessory bar (when given) above a centered status block.
 * Keeps the loading / error / not-found UI structurally aligned with
 * the loaded layout so the right pane doesn't jump when data arrives.
 */
function PrePaneState({
  rightPaneAccessory,
  children,
}: {
  rightPaneAccessory?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex h-full flex-col">
      {rightPaneAccessory}
      <div className="flex flex-1 items-center justify-center">{children}</div>
    </div>
  );
}
